import React, { useMemo, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Actions as ApiCallActions } from '../../../redux/apiCall/reducers'
import DataTable from '../../../components/data-table'
import Modal from '../../../components/modal'
import general from '../../../utils/general'
import RoleBasedAccess from '../../../components/role-based-access'
import { UserTypeEnum } from '../../../utils/enums'
import useI18n from '../../../i18n/useI18n'

const QuotaModal = ({ isOpen, onClose, program }) => {
  const { t, language } = useI18n()
  const dispatch = useDispatch()
  const [quotaData, setQuotaData] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingQuotaId, setEditingQuotaId] = useState(null)
  const [deletingQuotaId, setDeletingQuotaId] = useState(null)
  const [createForm, setCreateForm] = useState({
    academicYear: new Date().getFullYear(),
    quota: '',
    filledQuota: '',
    notes: ''
  })

  // Program değiştiğinde veya modal açıldığında verileri çek
  useEffect(() => {
    if (isOpen && program?.id) {
      fetchQuotas(program.id)
      setShowForm(false)
      setEditingQuotaId(null)
      setCreateForm({
        academicYear: new Date().getFullYear(),
        quota: '',
        filledQuota: '',
        notes: ''
      })
    }
  }, [isOpen, program?.id])

  // Modal kapandığında verileri temizle
  useEffect(() => {
    if (!isOpen) {
      setQuotaData([])
    }
  }, [isOpen])

  // Kontenjan bilgilerini getir
  const fetchQuotas = (programId) => {
    setLoading(true)
    dispatch(
      ApiCallActions.Get({
        url: `programs/${programId}/quotas`,
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const responseData = data?.data || data || []
          setQuotaData(Array.isArray(responseData) ? responseData : [])
          setLoading(false)
        },
        onError: () => {
          setQuotaData([])
          setLoading(false)
        }
      })
    )
  }

  const handleCreateChange = (field, value) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const resetCreateForm = () => {
    setCreateForm({
      academicYear: new Date().getFullYear(),
      quota: '',
      filledQuota: '',
      notes: ''
    })
  }

  const handleCreateSubmit = (e) => {
    e.preventDefault()
    if (!program?.id || submitLoading) return

    const academicYear = Number(createForm.academicYear)
    const quota = Number(createForm.quota)
    const filledQuota = createForm.filledQuota === '' ? 0 : Number(createForm.filledQuota)

    if (!academicYear || academicYear < 2000 || academicYear > 2100) {
      general.notificationError(t('QUOTA_NOTIF_INVALID_YEAR'))
      return
    }

    if (!quota || quota <= 0) {
      general.notificationError(t('QUOTA_NOTIF_INVALID_QUOTA'))
      return
    }

    if (filledQuota < 0) {
      general.notificationError(t('QUOTA_NOTIF_INVALID_FILLED'))
      return
    }

    setSubmitLoading(true)

    const payload = {
      academicYear,
      quota,
      filledQuota,
      notes: createForm.notes?.trim() || undefined
    }

    const request = editingQuotaId
      ? ApiCallActions.Put({
          url: `programs/${program.id}/quotas/${editingQuotaId}`,
          data: payload,
          showAlertOnError: true,
          showLoading: false,
          onSuccess: () => {
            general.messageSuccess(t('QUOTA_MSG_UPDATED'))
            resetCreateForm()
            setEditingQuotaId(null)
            setShowForm(false)
            fetchQuotas(program.id)
            setSubmitLoading(false)
          },
          onError: () => {
            setSubmitLoading(false)
          }
        })
      : ApiCallActions.Post({
          url: `programs/${program.id}/quotas`,
          data: payload,
          showAlertOnError: true,
          showLoading: false,
          onSuccess: () => {
            general.messageSuccess(t('QUOTA_MSG_CREATED'))
            resetCreateForm()
            setShowForm(false)
            fetchQuotas(program.id)
            setSubmitLoading(false)
          },
          onError: () => {
            setSubmitLoading(false)
          }
        })

    dispatch(request)
  }

  const handleEditClick = (row) => {
    setShowForm(true)
    setEditingQuotaId(row.id)
    setCreateForm({
      academicYear: row.academicYear ?? new Date().getFullYear(),
      quota: row.quota ?? '',
      filledQuota: row.filledQuota ?? '',
      notes: row.notes || ''
    })
  }

  const handleDeleteClick = async (row) => {
    if (!program?.id || !row?.id || deletingQuotaId === row.id) return

    const confirmed = await general.confirmDelete(t('QUOTA_ENTITY_NAME'), `${row.academicYear || row.id}`)
    if (!confirmed) return

    setDeletingQuotaId(row.id)

    dispatch(
      ApiCallActions.Delete({
        url: `programs/${program.id}/quotas/${row.id}`,
        showAlertOnError: true,
        showLoading: false,
        onSuccess: () => {
          general.showDeleteSuccess(t('QUOTA_ENTITY_NAME'))
          fetchQuotas(program.id)
          setDeletingQuotaId(null)
        },
        onError: () => {
          setDeletingQuotaId(null)
        }
      })
    )
  }

  // Doluluk yüzdesini hesapla
  const getOccupancyPercent = (filled, total) => {
    if (!total || total === 0) return 0
    return Math.round((filled / total) * 100)
  }

  // Doluluk durumuna göre renk
  const getOccupancyColor = (percent) => {
    if (percent >= 90) return '#ef4444' // Kırmızı - neredeyse dolu
    if (percent >= 70) return '#f59e0b' // Turuncu - dolmak üzere
    if (percent >= 50) return '#eab308' // Sarı - yarı dolu
    return '#22c55e' // Yeşil - müsait
  }

  // Tablo sütunları
  const columns = useMemo(() => [
    {
      key: 'academicYear',
      label: t('QUOTA_COLUMNS_ACADEMIC_YEAR'),
      render: (value) => (
        <span className="dt-badge dt-badge-primary">{value || '-'}</span>
      )
    },
    {
      key: 'quota',
      label: t('QUOTA_COLUMNS_TOTAL'),
      align: 'center',
      render: (value) => (
        <strong style={{ color: '#1e293b' }}>
          {value || '-'}
        </strong>
      )
    },
    {
      key: 'filledQuota',
      label: t('QUOTA_COLUMNS_FILLED'),
      align: 'center',
      render: (value) => (
        <span style={{ color: '#64748b' }}>
          {value ?? '-'}
        </span>
      )
    },
    {
      key: 'availableQuota',
      label: t('QUOTA_COLUMNS_AVAILABLE'),
      align: 'center',
      render: (value) => (
        <strong style={{ 
          color: value > 0 ? '#22c55e' : '#ef4444',
        
        }}>
          {value ?? '-'}
        </strong>
      )
    },
    {
      key: 'occupancy',
      label: t('QUOTA_COLUMNS_OCCUPANCY'),
      align: 'center',
      render: (_, row) => {
        const percent = getOccupancyPercent(row.filledQuota, row.quota)
        const color = getOccupancyColor(percent)
        
        return (
          <div className="quota-progress-wrapper">
            <div className="quota-progress-bar">
              <div 
                className="quota-progress-fill"
                style={{ 
                  width: `${percent}%`,
                  background: color
                }}
              />
            </div>
            <span className="quota-progress-text" style={{ color }}>
              %{percent}
            </span>
          </div>
        )
      }
    },
    {
      key: 'notes',
      label: t('QUOTA_COLUMNS_NOTES'),
      render: (value) => (
        <span style={{ color: '#64748b', fontSize: '13px' }}>
          {value || '-'}
        </span>
      )
    },
    {
      key: 'actions',
      label: t('QUOTA_COLUMNS_ACTIONS'),
      align: 'center',
      render: (_, row) => (
        <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
          <div className="d-flex justify-content-center" style={{ gap: '8px' }}>
            <button
              type="button"
              className="dt-btn dt-btn-secondary"
              onClick={() => handleEditClick(row)}
            >
              <i className="zmdi zmdi-edit"></i>
            </button>
            <button
              type="button"
              className="dt-btn dt-btn-danger"
              onClick={() => handleDeleteClick(row)}
              disabled={deletingQuotaId === row.id}
            >
              <i className="zmdi zmdi-delete"></i>
            </button>
          </div>
        </RoleBasedAccess>
      )
    }
  ], [t, language])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('QUOTA_MODAL_TITLE')}
      subtitle={general.getLocaleValue(program, 'name', language) || t('PROGRAMS_ENTITY').toLowerCase()}
      icon="zmdi-accounts"
      size="xlarge"
    >
      <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
        <div className="d-flex justify-content-between align-items-center m-b-15">
          <div>
            <h6 className="m-b-0">{editingQuotaId ? t('QUOTA_FORM_EDIT_TITLE') : t('QUOTA_FORM_NEW_TITLE')}</h6>
            <small className="text-muted">{t('QUOTA_FORM_DUPLICATE_WARNING')}</small>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-round btn-sm"
            onClick={() => {
              setShowForm((prev) => !prev)
              setEditingQuotaId(null)
              resetCreateForm()
            }}
          >
            <i className="zmdi zmdi-plus m-r-5"></i>
            {showForm ? t('QUOTA_FORM_HIDE') : t('QUOTA_FORM_ADD')}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleCreateSubmit} className="card p-3 m-b-20">
            <div className="row clearfix">
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('QUOTA_COLUMNS_ACADEMIC_YEAR')}</label>
                  <input
                    type="number"
                    className="form-control"
                    min="2000"
                    max="2100"
                    value={createForm.academicYear}
                    onChange={(e) => handleCreateChange('academicYear', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('QUOTA_COLUMNS_TOTAL')}</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    value={createForm.quota}
                    onChange={(e) => handleCreateChange('quota', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('QUOTA_FORM_FILLED_OPTIONAL')}</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    value={createForm.filledQuota}
                    onChange={(e) => handleCreateChange('filledQuota', e.target.value)}
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('QUOTA_FORM_NOTES_OPTIONAL')}</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength="150"
                    value={createForm.notes}
                    onChange={(e) => handleCreateChange('notes', e.target.value)}
                    placeholder={t('QUOTA_FORM_NOTES_PLACEHOLDER')}
                  />
                </div>
              </div>
            </div>

            <div className="text-right">
              <button
                type="button"
                className="btn btn-default btn-round m-r-10"
                onClick={resetCreateForm}
                disabled={submitLoading}
              >
                {t('QUOTA_FORM_CLEAR')}
              </button>
              <button
                type="submit"
                className="btn btn-success btn-round"
                disabled={submitLoading}
              >
                {submitLoading ? t('QUOTA_FORM_SAVING') : editingQuotaId ? t('QUOTA_FORM_UPDATE') : t('QUOTA_FORM_SAVE')}
              </button>
            </div>
          </form>
        )}
      </RoleBasedAccess>

      <DataTable
        columns={columns}
        data={quotaData}
        loading={loading}
        emptyMessage={t('QUOTA_EMPTY_MESSAGE')}
        emptyIcon="zmdi-accounts"
        rowKey="id"
        itemLabel={t('QUOTA_ITEM_LABEL')}
        pagination={{
          enabled: true,
          defaultItemsPerPage: 5,
          itemsPerPageOptions: [5, 10, 25],
          showInfo: true
        }}
        hoverable={true}
      />

      <style>{`
        .quota-progress-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .quota-progress-bar {
          flex: 1;
          height: 8px;
          background: #e2e8f0;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .quota-progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease;
        }
        
        .quota-progress-text {
          font-size: 13px;
          font-weight: 600;
          min-width: 40px;
          text-align: right;
        }
      `}</style>
    </Modal>
  )
}

export default QuotaModal

